import { useEffect, useState } from "react";
import posthog from "posthog-js";

import { isFeatureEnabled, isFeatureExplicitlySet } from "components/featureFlags";

/**
 * A feature flag that can be flipped from PostHog, without a deploy.
 *
 * The existing `isFeatureEnabled` in components/Features is per-device and opt-in: a
 * flag is on only if THIS browser has the URL param or the localStorage key. That is
 * the right tool for a developer toggle (`chatAIDebugMode`, `subscriptionDebugging`),
 * which is what all 22 of its call sites are. It cannot do the other job — expose
 * something to a percentage of real visitors, watch the numbers, and switch it off
 * again — because there is no way to reach anyone else's localStorage.
 *
 * This hook does that job and keeps the old one working:
 *
 *   1. A local override always wins. Setting the flag in this browser (`?myFlag` or
 *      localStorage) turns it on regardless of PostHog, and explicitly setting it to
 *      "false" turns it off regardless. Developers keep the workflow they have, and
 *      can always overrule a rollout locally to reproduce a report.
 *   2. Otherwise PostHog decides, so the rollout percentage and the kill switch live
 *      in one place that is not a deploy.
 *   3. Until PostHog answers — and if it never does — `fallback` decides.
 *
 * `fallback` is per-call rather than a global default, because the right answer differs
 * by flag. A rollout flag on a signup path should fail OPEN: if the flags never arrive,
 * hiding the CTA silently costs conversions and looks like nothing at all. A debug or
 * admin toggle must fail CLOSED. There is no safe blanket choice, so the caller states
 * one.
 *
 * Returns `{ enabled, isResolved }`. `isResolved` is false while PostHog is still being
 * waited on, which lets a caller render nothing rather than flash the fallback and then
 * swap. On a statically-rendered page that flash is otherwise unavoidable — flags are
 * fetched in the browser, after the HTML is already on screen.
 */

/**
 * How long to wait for PostHog before falling back.
 *
 * PostHog is proxied first-party through CloudFront (`api_host: ${SITE_ROOT}/pd`, see
 * gatsby-browser.js), so an ad blocker usually does NOT stop it — but "usually" is not
 * "always", and a blocked request fails by never resolving rather than by erroring.
 * Without a deadline a caller waiting on `isResolved` would wait forever.
 */
export const FLAG_RESOLVE_TIMEOUT_MS = 3000;

/**
 * A variant forced on this browser, or null.
 *
 * Shares the key namespace with the boolean flags, and stays unambiguous by ignoring
 * "true"/"false" — those belong to `enableFeature`/`disableFeature`, and neither is the
 * name of a variant. So `?myFlag=test` forces an arm while `?myFlag` still means the
 * boolean sense, and the two can never be read as each other.
 */
const localVariantOverride = (featureName) => {
  if (typeof window === "undefined") {
    return null;
  }
  const isVariantName = (value) =>
    Boolean(value) && value !== "true" && value !== "false";

  // Applied to the URL too, not just localStorage. `?myFlag=true` is the boolean
  // sense of the key; returning it here would hand the experiment a variant named
  // "true" -- the exact confusion the shared namespace is meant to avoid.
  const fromUrl = new URLSearchParams(window.location.search).get(featureName);
  if (isVariantName(fromUrl)) {
    return fromUrl;
  }
  const stored = window.localStorage.getItem(featureName);
  return isVariantName(stored) ? stored : null;
};

export const useFeatureFlag = (featureName, { fallback = false } = {}) => {
  // Seeded from the local override so a developer's own toggle is honoured on the very
  // first render, with no wait and no flash.
  const [state, setState] = useState(() => ({
    // `|| fallback` here was wrong: with a fail-open flag (fallback: true) an
    // explicitly DISABLED flag seeded as enabled, while isResolved seeded true --
    // so a caller trusting isResolved rendered the feature on despite being told
    // not to, then flashed off when the effect corrected it. The override decides
    // outright, and fallback speaks only when there is no override.
    enabled: isFeatureExplicitlySet(featureName)
      ? isFeatureEnabled(featureName)
      : fallback,
    isResolved: isFeatureExplicitlySet(featureName),
  }));

  useEffect(() => {
    if (isFeatureExplicitlySet(featureName)) {
      // Local override wins outright; nothing to wait for.
      setState({ enabled: isFeatureEnabled(featureName), isResolved: true });
      return undefined;
    }

    // Two separate guards, because they answer different questions. `mounted` stops
    // any write after unmount. `answered` stops only the DEADLINE from overriding a
    // real answer. A single one-shot guard conflated them and pinned the first answer
    // for the life of the page -- but onFeatureFlags fires again on every later reload
    // of the flags, which is how a login re-buckets someone and how a kill switch
    // reaches a page that is already open.
    let mounted = true;
    let answered = false;

    const applyAnswer = (enabled) => {
      if (!mounted) {
        return;
      }
      answered = true;
      setState({ enabled, isResolved: true });
    };

    const applyFallback = () => {
      if (!mounted || answered) {
        return;
      }
      setState({ enabled: fallback, isResolved: true });
    };

    // Not wrapped in try/catch. posthog-js does not throw when init() was never
    // called -- measured against the pinned 1.399.2: onFeatureFlags returns an
    // unsubscribe function and isFeatureEnabled returns undefined. init() IS skipped
    // in local dev unless ENABLE_POSTHOG_IN_DEVELOPMENT is set (gatsby-browser.js),
    // and the skipped case needs no guard: the callback simply never fires, and the
    // deadline below answers with the fallback.
    const unsubscribe = posthog.onFeatureFlags(() => {
      const value = posthog.isFeatureEnabled(featureName);
      // `undefined` means PostHog has no flag by this name -- which is NOT the same
      // as a flag it evaluated to off. Measured against the real project: an unknown
      // key returns undefined, a known-but-off key returns false.
      applyAnswer(value === undefined ? fallback : Boolean(value));
    });

    const timer = setTimeout(applyFallback, FLAG_RESOLVE_TIMEOUT_MS);

    return () => {
      // Cleared first so neither the callback nor the timer can write after unmount.
      mounted = false;
      clearTimeout(timer);
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [featureName, fallback]);

  return state;
};

/**
 * The variant a visitor is in, for a multivariate flag or a PostHog experiment.
 *
 * `useFeatureFlag` cannot do this job. It asks `posthog.isFeatureEnabled`, which answers
 * true/false — so every arm of an A/B test collapses to "on" and the one thing the test
 * exists to measure, WHICH arm someone saw, is lost. Experiments need the variant key
 * (`control`, `test`, ...), which is what `getFeatureFlag` returns.
 *
 * Resolution mirrors useFeatureFlag exactly: a local override wins, then PostHog, then
 * `fallback`. The override is any non-boolean value on the same key — `?myFlag=test` or
 * `localStorage.myFlag = "test"` — so one namespace serves both hooks without a boolean
 * "true"/"false" ever being mistaken for a variant name.
 *
 * `fallback` defaults to null rather than to a variant name on purpose: guessing
 * `"control"` would silently fold everyone into the baseline arm when PostHog is
 * unreachable, quietly biasing a result that looks perfectly healthy. Null forces the
 * caller to decide what an unmeasurable visitor should see.
 *
 * Exposure is NOT recorded by this hook. posthog-js sends `$feature_flag_called` -- the
 * event an experiment counts a person from -- on every `getFeatureFlag` call unless told
 * not to, and this hook reads the flag on every reload of the flags, including ones the
 * page has decided to ignore (a visitor who touched the control before the flags arrived,
 * or an answer that lands after FLAG_RESOLVE_TIMEOUT_MS). Recording exposure there would
 * count someone in `test` for a page that showed control (review, cca-app#839). So every
 * read passes `send_event: false`, and the caller records exposure with
 * `recordFeatureVariantExposure` at the moment it commits to an arm. `source` says where
 * the variant came from, so a caller records exposure only for a PostHog answer: an
 * override or the fallback is not an assignment.
 */
export const useFeatureVariant = (featureName, { fallback = null } = {}) => {
  const [state, setState] = useState(() => {
    const override = localVariantOverride(featureName);
    return override !== null
      ? { variant: override, isResolved: true, source: "override" }
      : { variant: fallback, isResolved: false, source: "fallback" };
  });

  useEffect(() => {
    const override = localVariantOverride(featureName);
    if (override !== null) {
      setState({ variant: override, isResolved: true, source: "override" });
      return undefined;
    }

    // See the boolean hook: `mounted` guards writes after unmount, `answered` guards
    // only the deadline. Re-bucketing on login has to reach an open page.
    let mounted = true;
    let answered = false;

    const applyAnswer = (variant, source) => {
      if (!mounted) {
        return;
      }
      answered = true;
      setState({ variant, isResolved: true, source });
    };

    const applyFallback = () => {
      if (!mounted || answered) {
        return;
      }
      setState({ variant: fallback, isResolved: true, source: "fallback" });
    };

    // See the boolean hook: no try/catch, because an uninitialised posthog-js does
    // not throw -- it just never calls back, which the deadline below covers.
    const unsubscribe = posthog.onFeatureFlags(() => {
      // send_event: false -- see the hook's comment; exposure is the caller's call.
      const value = posthog.getFeatureFlag(featureName, { send_event: false });
      // A multivariate flag answers with the variant key. A boolean flag answers
      // true/false, and neither is a variant -- treat that as "not in the test".
      if (typeof value === "string") {
        applyAnswer(value, "posthog");
      } else {
        applyAnswer(fallback, "fallback");
      }
    });

    const timer = setTimeout(applyFallback, FLAG_RESOLVE_TIMEOUT_MS);

    return () => {
      mounted = false;
      clearTimeout(timer);
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [featureName, fallback]);

  return state;
};

/**
 * Record that this visitor was shown the arm PostHog assigned for `featureName`.
 *
 * `getFeatureFlag` with the default `send_event` is how posthog-js emits
 * `$feature_flag_called`, the event an experiment counts a person from; it reads the
 * value already loaded, so this sends the same arm the hook reported. Call it once,
 * at the moment the page commits to an arm, and only when the hook's `source` is
 * "posthog" -- an override or a fallback is not an assignment to record.
 */
export const recordFeatureVariantExposure = (featureName) => {
  posthog.getFeatureFlag(featureName);
};

export default useFeatureFlag;
